<template>
  <div>
    <div class="flex items-center margin-bottom-md">
      <button
        class="tb-btn tb-btn-contained medium-em"
        title="Sync element with updated sheet data"
        :disabled="syncing"
        data-entity="producer-editor-syncData"
        data-test="producer-editor-sheetData-syncButton"
        @click="syncData"
      >
        {{ syncing ? 'Syncing...' : 'Sync Data' }}
      </button>

      <div class="margin-left-md" data-test="producer-editor-sheetData-lastSync">
        Last manual sync {{ lastSyncLabel }}
      </div>
    </div>

    <div v-if="url" class="margin-bottom-md">
      <div class="tb-label">
        Spreadsheet
      </div>

      <div class="flex items-center">
        <span class="margin-right-sm" data-test="producer-editor-sheetData-workbookName">
          {{ workbookName }}
        </span>

        <a
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          data-entity="producer-editor-openWorkbook"
          data-test="producer-editor-sheetData-openWorkbook"
        >
          <i class="fas fa-external-link"></i>
        </a>
      </div>
    </div>

    <div v-if="error" class="text-danger" data-test="producer-editor-sheetData-error">
      {{ error }}
    </div>

    <hr />

    <div class="tb-form-group">
      <div
        class="tb-checkbox"
        title="Automatically update output when sheet changes are detected"
      >
        <input
          type="checkbox"
          id="pollingEnabled"
          v-model="pollingEnabled"
          :disabled="!isPrivateSheet"
          data-entity="producer-editor-togglePolling"
          data-test="producer-editor-sheetData-autoSyncToggle"
        >

        <label
          class="checkbox with-description"
          for="pollingEnabled"
        >
          Auto Sync {{ isPrivateSheet ? '' : '(Private Sheets Only)' }}

          <div class="description">
            Automatically update output when sheet changes are detected
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { fetch, buildQueryString } from '../../../../utils/request';

export default {
  props: {
    element: {
      required: true,
    },

    template: {
      required: true,
    },

    graphic_fields: {
      required: true,
    },
  },

  data() {
    return {
      error: null,
      lastSync: this.element?.data?.lastSync,
      now: Date.now(),
      syncing: false,
    };
  },

  mounted() {
    this.interval = setInterval(() => {
      this.now = Date.now();
    }, 30e3);
  },

  beforeDestroy() {
    window.clearInterval(this.interval);
  },

  computed: {
    isPrivateSheet() {
      return this.element.data.type === 'private-google-sheet';
    },

    pollingEnabled: {
      get() {
        if (this.isPrivateSheet) {
          return this.graphic_fields[this.element.id]?.pollingEnabled ?? false;
        }

        return false;
      },
      set(v) {
        this.updateElement('pollingEnabled', v);
      },
    },

    url() {
      const { workbookId } = this.element.data;

      if (!workbookId) {
        return '';
      }

      return `https://docs.google.com/spreadsheets/d/${workbookId}`;
    },

    workbookName() {
      const { workbookName } = this.element.data;
      return workbookName || '';
    },

    /**
     * Get the amount of time since last sync and pretty it up.
     * Can probably be broken out to a shared util if needed elsewhere.
     */
    lastSyncLabel() {
      const lastSync = this.lastSync || this.element.data?.lastSync;
      if (!lastSync) {
        return '';
      }

      const delta = (this.now - lastSync) / 1e3;
      let seconds = Math.floor(delta);

      if (seconds <= 0) {
        return 'less than a minute ago';
      }

      let minutes = Math.floor(seconds / 60);
      seconds -= (minutes * 60);

      let hours = Math.floor(minutes / 60);
      minutes -= (hours * 60);

      const days = Math.floor(hours / 24);
      hours -= (days * 24);

      if (days > 7) {
        // Just show the date
        // TODO: Format prettier
        return new Date(lastSync).toISOString();
      }

      if (days >= 1) {
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }

      if (hours >= 1) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }

      if (minutes >= 1) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }

      return 'less than a minute ago';
    },
  },

  methods: {
    updateElement(field, value) {
      this.$emit('value', { field, value });
    },

    setError(err) {
      clearTimeout(this.errorTimeout);
      this.error = err;

      this.errorTimeout = setTimeout(() => {
        this.error = null;
      }, 5e3);
    },

    async syncData() {
      this.syncing = true;

      const { accountId } = this.element.data;

      try {
        let success;
        if (accountId) {
          success = await this.syncPrivateGoogleSheet();
        } else {
          success = await this.syncPublicGoogleSheet();
        }

        if (success) {
          await this.setLastSync();

          // Set a "field" on the element so it knows to update in tgb-live
          this.$emit('value', {
            field: 'data.lastSync',
            value: this.lastSync,
          });
        }
      } catch (err) {
        console.log('Error syncing data:', err); // eslint-disable-line
        this.setError(err && err.message);
      } finally {
        this.syncing = false;
      }
    },

    /**
     * Update lastSync on sheet-data element on graphic template
     */
    async setLastSync() {
      this.now = Date.now();
      this.lastSync = this.now;

      fetch(`${process.env.TAGBOARD_GRAPHICS_URL}/template/${this.template._id}/element`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          owner: this.template.owner,

          // Update is partial based, so only need to provide the changes
          element: {
            id: this.element.id,
            data: {
              ...this.element.data,
              lastSync: this.now,
            },
          },
        }),
      });
    },

    async syncPrivateGoogleSheet() {
      const {
        workbookId,
        accountId,
        sheetId,
      } = this.element.data;

      try {
        await fetch(`${process.env.GOOGLE_API}/sheets/${accountId}/${workbookId}/${sheetId}?cache=false`);
      } catch (err) {
        this.setError('Failed to sync data');
        return false;
      }

      return true;
    },

    /**
     * Hit endpoint used to fetch sheet data using a "force" flag,
     * so the data is refetched from google.
     * This new data will be cached and ready to use by tgb-live.
     */
    async syncPublicGoogleSheet() {
      const { url, sheet } = this.element.data;

      const querystring = [
        `key=${this.element.id}`,
        `url=${encodeURIComponent(url)}`,
        `sheet=${encodeURIComponent(sheet)}`,
        'force=true',
      ].join('&');

      const resp = await fetch(`${process.env.GOOGLE_API}/sheets/url?${querystring}`, {
        mode: 'cors',
        credentials: 'include',
      });

      if (!resp) {
        this.setError('Failed to sync data');
        return false;
      }

      return true;
    },
  },
};
</script>
